import React from 'react';
import {
  Accordion as MuiAccordion,
  AccordionSummary,
  AccordionDetails,
  AccordionProps as MuiAccordionProps,
  Typography,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { StyledAccordion, StyledAccordionSummary, StyledAccordionDetails } from './Accordion.style';

export interface AccordionItem {
  id: string;
  title: string;
  content: React.ReactNode;
  disabled?: boolean;
  defaultExpanded?: boolean;
}

export interface AccordionProps extends Omit<MuiAccordionProps, 'children' | 'onChange'> {
  /**
   * Array of accordion items
   */
  items?: AccordionItem[];
  /**
   * Whether multiple panels can be expanded at once
   */
  multiple?: boolean;
  /**
   * Callback fired when an accordion panel is expanded/collapsed
   */
  onChange?: (panelId: string, expanded: boolean) => void;
  /**
   * Custom expand icon
   */
  expandIcon?: React.ReactNode;
  /**
   * Whether to show border between items
   */
  bordered?: boolean;
  /**
   * Size variant
   */
  size?: 'small' | 'medium' | 'large';
  /**
   * For single accordion usage
   */
  children?: React.ReactNode;
  /**
   * Title for single accordion
   */
  title?: string;
}

export const Accordion: React.FC<AccordionProps> = ({
  items,
  multiple = false,
  onChange,
  expandIcon = <ExpandMore />,
  bordered = true,
  size = 'medium',
  children,
  title,
  ...props
}) => {
  const [expandedPanels, setExpandedPanels] = React.useState<string[]>([]);

  React.useEffect(() => {
    if (items) {
      const defaultExpanded = items
        .filter(item => item.defaultExpanded)
        .map(item => item.id);
      setExpandedPanels(defaultExpanded);
    }
  }, [items]);

  const handleExpansion = (panelId: string) => (
    event: React.SyntheticEvent,
    isExpanded: boolean
  ) => {
    if (multiple) {
      setExpandedPanels(prev =>
        isExpanded
          ? [...prev, panelId]
          : prev.filter(id => id !== panelId)
      );
    } else {
      setExpandedPanels(isExpanded ? [panelId] : []);
    }

    if (onChange) {
      onChange(panelId, isExpanded);
    }
  };

  // Single accordion usage
  if (children && title) {
    return (
      <StyledAccordion
        bordered={bordered}
        size={size}
        {...props}
      >
        <StyledAccordionSummary
          expandIcon={expandIcon}
          size={size}
        >
          <Typography variant={size === 'large' ? 'h6' : 'body1'}>
            {title}
          </Typography>
        </StyledAccordionSummary>
        <StyledAccordionDetails size={size}>
          {children}
        </StyledAccordionDetails>
      </StyledAccordion>
    );
  }

  // Multiple accordions usage
  if (!items) {
    return null;
  }

  return (
    <>
      {items.map((item) => (
        <StyledAccordion
          key={item.id}
          expanded={expandedPanels.includes(item.id)}
          onChange={handleExpansion(item.id)}
          disabled={item.disabled}
          bordered={bordered}
          size={size}
          {...props}
        >
          <StyledAccordionSummary
            expandIcon={expandIcon}
            size={size}
          >
            <Typography variant={size === 'large' ? 'h6' : 'body1'}>
              {item.title}
            </Typography>
          </StyledAccordionSummary>
          <StyledAccordionDetails size={size}>
            {item.content}
          </StyledAccordionDetails>
        </StyledAccordion>
      ))}
    </>
  );
}; 